import { useEffect, useContext, useState } from 'react';
import { LoadingContext } from '../contexts/LoadingContextProvider';
import { apigeeEndpoint } from '../apis';
import { EnhancedAmendFailureDynamoItem } from '../routes/amend-failures/amend-failures';
import { useQueryClient } from 'react-query';
import { useIntl } from 'react-intl';

export type UseAmendFailuresResponse = {
  amendFailuresResponse?: EnhancedAmendFailureDynamoItem[];
  setAmendFailuresRequest: (amendFailuresRequest: AmendFailuresRequest) => void;
  amendFailuresRequest: AmendFailuresRequest;
  errorMessage: string;
  searchDone: boolean | undefined;
};

export type AmendFailuresRequest = {
  recordLocator: string;
  product: string;
  archive: boolean;
};

export const useAmendFailures = (): UseAmendFailuresResponse => {
  const queryClient = useQueryClient();
  const [amendFailuresRequest, setAmendFailuresRequest] = useState<AmendFailuresRequest>();
  const [amendFailuresResponse, setAmendFailuresResponse] = useState<EnhancedAmendFailureDynamoItem[]>([]);
  const { showLoading, closeLoading } = useContext(LoadingContext);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [searchDone, setSearchDone] = useState<boolean | undefined>(undefined);
  const intl = useIntl();

  useEffect(() => {
    const getAmendFailures = async (amendFailuresRequest: AmendFailuresRequest) => {
      const { recordLocator, product, archive } = amendFailuresRequest;
      showLoading(intl.formatMessage({ id: 'amend-failure.table.searching' }));

      const searchAllProducts = product.toUpperCase() === 'ALL' || product === '';
      const urlParams = `${searchAllProducts ? '*' : product}/${recordLocator}`;

      await queryClient.fetchQuery(
        'amendFailures',
        async () => {
          const response = await apigeeEndpoint.get<EnhancedAmendFailureDynamoItem[] | { errorMessage: string }>(
            archive ? `amend-failure/archive/${urlParams}` : `amend-failure/${urlParams}`,
          );
          setSearchDone(true);
          if ('errorMessage' in response.data) {
            setAmendFailuresResponse([]);
            return [];
          }
          const enhancedAmendFailureDynamoItems: EnhancedAmendFailureDynamoItem[] = response.data.map(item => {
            const action = item.status === 'NEW' ? `${item.recordLocator}#${item.createdAt}` : undefined;
            return { ...item, action };
          });

          setErrorMessage('');
          setAmendFailuresResponse(enhancedAmendFailureDynamoItems);
          return response.data;
        },
        {
          retry: 2,
          staleTime: 1000,
          cacheTime: 50000,

          initialData: () => {
            return queryClient.getQueryData('amendFailures');
          },
        },
      );
      closeLoading();
    };

    if (
      amendFailuresRequest &&
      (amendFailuresRequest.recordLocator || amendFailuresRequest.product || amendFailuresRequest.archive)
    ) {
      if (amendFailuresRequest.recordLocator !== '' && amendFailuresRequest.recordLocator.length !== 6) {
        setErrorMessage('PNR IS NOT VALID');
        setSearchDone(true);
        setAmendFailuresResponse([]);
        return;
      }
      void getAmendFailures(amendFailuresRequest);
    }
  }, [amendFailuresRequest]);

  return { setAmendFailuresRequest, amendFailuresRequest, amendFailuresResponse, errorMessage, searchDone };
};
